import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Bell, Play } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  academyGetNotifSettings, academyPutNotifSettings, academyRunNotifSweep,
} from '../../../services/api';

/** Admin → Academy → Notification settings + manual sweep trigger. */
export default function NotificationSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { (async () => setS(await academyGetNotifSettings()))(); }, []);
  if (!s) return <div className="p-6 text-slate-500">Loading…</div>;

  const save = async () => {
    setSaving(true);
    try { await academyPutNotifSettings(s); toast.success('Saved'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  const sweep = async () => {
    try {
      const r = await academyRunNotifSweep();
      toast.success(`Swept — sent ${JSON.stringify(r.sent)}`);
    } catch (e) { toast.error('Sweep failed'); }
  };

  return (
    <div className="p-6 space-y-4 max-w-2xl" data-testid="academy-notif-settings">
      <div className="flex items-center gap-2 text-slate-500 uppercase text-xs tracking-widest">
        <Bell className="h-3.5 w-3.5" />Lutrom Academy
      </div>
      <h1 className="text-2xl font-display font-bold">Notification settings</h1>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={s.enabled}
          onChange={(e) => setS({ ...s, enabled: e.target.checked })}
          data-testid="notif-enabled" />
        Notifications enabled
      </label>
      <label className="flex flex-col text-sm">
        Send "due soon" this many days before target completion:
        <input type="number" min={1} max={90} value={s.due_soon_days_before}
          onChange={(e) => setS({ ...s, due_soon_days_before: parseInt(e.target.value) || 0 })}
          className="border rounded px-2 py-1 mt-1 w-32" data-testid="notif-due-soon" />
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={s.overdue_check_enabled}
          onChange={(e) => setS({ ...s, overdue_check_enabled: e.target.checked })}
          data-testid="notif-overdue-check" />
        Also notify when past target completion date
      </label>
      <label className="flex flex-col text-sm">
        Send "refresher due" this many days before the certificate refresher date:
        <input type="number" min={1} max={180} value={s.refresher_days_before}
          onChange={(e) => setS({ ...s, refresher_days_before: parseInt(e.target.value) || 0 })}
          className="border rounded px-2 py-1 mt-1 w-32" data-testid="notif-refresher-days" />
      </label>
      <div className="flex gap-2 pt-2">
        <Button onClick={save} disabled={saving} data-testid="notif-save">Save</Button>
        <Button variant="outline" onClick={sweep} data-testid="notif-sweep">
          <Play className="h-3 w-3 mr-1" />Run sweep now
        </Button>
      </div>
      <p className="text-xs text-slate-500">
        A daily cron (`.emergent/crons.yml`) invokes the sweep automatically; the button above forces
        an out-of-schedule run for testing.
      </p>
    </div>
  );
}
